import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, Tab, Box } from '@mui/material';

export type StudyTab = 'transcript' | 'translation' | 'vocabulary';
export const STUDY_TABS_TOUCH_TARGET_MIN_HEIGHT = 44;

interface VideoPlayerStudyTabsProps {
  /** Currently active study tab. */
  activeTab: StudyTab;
  /** Called when the user clicks a tab. */
  onTabChange: (tab: StudyTab) => void;
}

/**
 * VideoPlayerStudyTabs — MUI tab switcher between the Transcript and Translation panels.
 *
 * Story 3.3 scope: allows users to switch between the transcript (Story 3.1/3.2)
 * and translation (Story 3.3) study surfaces below the video player.
 *
 * Design notes:
 * - Lightweight wrapper around MUI Tabs; parent (VideoPlayer) owns the active tab state.
 * - Uses i18n keys 'transcriptTab' and 'translationTab' from the 'player' namespace.
 * - Compact variant so tabs don't dominate vertical space.
 */
export const VideoPlayerStudyTabs: FC<VideoPlayerStudyTabsProps> = ({
  activeTab,
  onTabChange,
}) => {
  const { t } = useTranslation('player');

  const handleChange = (_: React.SyntheticEvent, value: StudyTab) => {
    onTabChange(value);
  };

  return (
    <Box
      sx={{ borderBottom: 1, borderColor: 'divider' }}
      data-testid="study-tabs"
    >
      <Tabs
        value={activeTab}
        onChange={handleChange}
        variant="fullWidth"
        aria-label={t('studyTabsLabel', 'Study tabs')}
        sx={{ minHeight: STUDY_TABS_TOUCH_TARGET_MIN_HEIGHT }}
      >
        <Tab
          label={t('transcriptTab')}
          value="transcript"
          data-testid="study-tab-transcript"
          sx={{ minHeight: STUDY_TABS_TOUCH_TARGET_MIN_HEIGHT, py: 0.75 }}
        />
        <Tab
          label={t('translationTab')}
          value="translation"
          data-testid="study-tab-translation"
          sx={{ minHeight: STUDY_TABS_TOUCH_TARGET_MIN_HEIGHT, py: 0.75 }}
        />
        <Tab
          label={t('vocabularyTab')}
          value="vocabulary"
          data-testid="study-tab-vocabulary"
          sx={{ minHeight: STUDY_TABS_TOUCH_TARGET_MIN_HEIGHT, py: 0.75 }}
        />
      </Tabs>
    </Box>
  );
};
